/* ------------------------------------------------------------------
   DynamicUI.jsx – adaptive UI grid w/ react-grid-layout + edit mode
------------------------------------------------------------------ */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ripples, mirage, jelly, jellyTriangle, chaoticOrbit } from 'ldrs';
import {
  Squares2X2Icon as Squares2X2Solid,
  QueueListIcon as QueueListSolid,
  PencilSquareIcon,
  CheckIcon,
  ArrowDownRightIcon,
  SquaresPlusIcon
} from '@heroicons/react/24/solid';
import { setTrailOpen } from '../../../../redux/slices/uiSlice.js';

import ReportCard from '../message-components/report-card/ReportCard.jsx';
import GraphCard from '../message-components/graph-card/GraphCard.jsx';
import GridCard from '../message-components/grid-card/GridCard.jsx';
import RecordCard from '../message-components/record-card/RecordCard.jsx';
import ReasoningCard from '../message-components/reasoning-card/ReasoningCard.jsx';
import Trail from '../message-components/reasoning-card/Trail.jsx';
import NumberCard from '../message-components/number-card/NumberCard.jsx';
import HiddenCardTray from './HiddenCardTray.jsx'; /* NEW */
import SpreadsheetCard from '../message-components/spreadsheet-card/SpreadsheetCard.jsx';
import BatchCreateCard from '../message-components/batch-create/BatchCreateCard.jsx';

import { useSocket } from '../../../../socket/socketContext';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { injectDynamicUICSS, removeDynamicUICSS } from './dynamicUtils.js';

import {
  updateGridItemRect,
  updateUiLayout
} from '../../../../redux/slices/sessionSlice.js';
import { setIsEditingUi } from '../../../../redux/slices/dynamicUiSlice.js';

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS_PX = { lg: 1200, md: 1200, sm: 1200, xs: 1200, xxs: 1200 };
const COLS = { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 };



const ROW_HEIGHT = 28;
const MARGIN = [16, 16];
const CONTAINER_PAD = [16, 16];

const EXTRA_BOTTOM_ROWS = 3;
const BOTTOM_SPACER_PX =
  EXTRA_BOTTOM_ROWS * ROW_HEIGHT +
  Math.max(0, EXTRA_BOTTOM_ROWS - 1) * MARGIN[1];

const TYPE_MIN = {
  number: { minW: 2, minH: 2 },
  graph:  { minW: 4, minH: 5 },
  grid:   { minW: 4, minH: 4 },
  report: { minW: 6, minH: 7 },
  record: { minW: 4, minH: 5 },
  spreadsheet: { minW: 6, minH: 7 },
};

const pickRect = (li) => ({ i: li.i, x: li.x, y: li.y, w: li.w, h: li.h });
const rectsById = (arr = []) => Object.fromEntries(arr.map(r => [r.i, { x: r.x, y: r.y, w: r.w, h: r.h }]));
const bps = ['lg', 'md', 'sm', 'xs', 'xxs'];

function DynamicUI() {
  const dispatch = useDispatch();
  const socket = useSocket?.();

  useEffect(() => {
    ripples.register();
    mirage.register();
    jelly.register();
    jellyTriangle.register();
    chaoticOrbit.register();
    injectDynamicUICSS(document);
    return () => removeDynamicUICSS(document);
  }, []);

  const { messages, layout } = useSelector(s => s.chat.chatSession);
  const { isGenerating, isLayoutGenerating } = useSelector(s => s.generating);
  const isDashboard = useSelector(s => s.ui.viewMode) === 'dashboard';
  const hoverLink = useSelector(s => s.ui.hoverLink);
  const isEditingUi = useSelector(s => s.dynamicUi.isEditingUi);

  const [currentBp, setCurrentBp] = useState('lg');

  const scrollRef = useRef(null);
  const frameRef = useRef(null);
  const widthRef = useRef(0);

  const lastLayoutsRef = useRef({ lg: [], md: [], sm: [], xs: [], xxs: [] });
  const touchedMapRef = useRef(new Map());

  const gridLayout = layout?.gridLayout || [];
  const visibleItems = gridLayout.filter(it => !it.hidden);
  const hiddenItems  = gridLayout.filter(it => it.hidden);
  const isEmpty = !visibleItems.length;

  const msgLookup = useMemo(
    () => Object.fromEntries((messages || []).map(m => [m.messageId, m])),
    [messages]
  );

  const showReasoning = isDashboard && isGenerating;
  const gridPaddingClass = showReasoning ? 'pb-[34vh]' : '';
  const canEditNow = isEditingUi && !isLayoutGenerating;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [visibleItems.length, hiddenItems.length]);

  const [editBgStyle, setEditBgStyle] = useState(null);
  useEffect(() => {
    if (!frameRef.current) return;
    const el = frameRef.current;
    const ro = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect?.width || el.clientWidth || 0;
      widthRef.current = width;

      if (!canEditNow) {
        setEditBgStyle(null);
        return;
      }

      const cols = COLS[currentBp] || 12;
      const colW = Math.max(
        1,
        (width - CONTAINER_PAD[0] * 2 - (cols - 1) * MARGIN[0]) / cols
      );
      const colStep = Math.max(1, Math.round(colW + MARGIN[0]));
      const rowStep = Math.max(1, Math.round(ROW_HEIGHT + MARGIN[1]));

      const lineColor = 'rgba(99,102,241,0.15)';
      const style = {
        backgroundImage: `
          repeating-linear-gradient(to right,
            ${lineColor}, ${lineColor} 1px, transparent 1px, transparent ${colStep}px
          ),
          repeating-linear-gradient(to bottom,
            ${lineColor}, ${lineColor} 1px, transparent 1px, transparent ${rowStep}px
          )
        `,
        backgroundSize: 'auto',
        backgroundOrigin: 'content-box',
      };
      setEditBgStyle(style);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [currentBp, canEditNow]);

  const ResponsiveLayouts = useMemo(() => {
    const base = { lg: [], md: [], sm: [], xs: [], xxs: [] };
    for (const item of visibleItems) {
      const { messageId, lg, md, sm, xs, xxs } = item;
      const msg = msgLookup[messageId];
      if (!msg || !lg) continue;

      const type = msg.type || 'grid';
      const locked = !!msg.locked;
      const pinned = !!item.pinned;
      const { minW = 1, minH = 1 } = TYPE_MIN[type] || {};

      const mk = (bpVals) => ({
        i: messageId,
        x: bpVals.x, y: bpVals.y, w: bpVals.w, h: bpVals.h,
        minW, minH,
        static: pinned || locked || !canEditNow,
        isDraggable: canEditNow && !locked && !pinned,
        isResizable: canEditNow && !locked && !pinned,
      });

      base.lg.push(mk(lg));
      if (md) base.md.push(mk(md));
      if (sm) base.sm.push(mk(sm));
      if (xs) base.xs.push(mk(xs));
      if (xxs) base.xxs.push(mk(xxs));

      for (const bp of ['md', 'sm', 'xs', 'xxs']) {
        if (!item[bp]) {
          const cols = COLS[bp];
          const w = Math.min(lg.w, cols);
          const x = Math.min(Math.max(0, lg.x), Math.max(0, cols - w));
          const clone = { ...lg, w, x };
          base[bp].push(mk(clone));
        }
      }
    }
    for (const bp of Object.keys(base)) {
      base[bp].sort((a, b) => (a.y - b.y) || (a.x - b.x));
    }
    return base;
  }, [visibleItems, msgLookup, canEditNow]);

  useEffect(() => {
    if (!isEditingUi) return;
    const seed = {};
    for (const bp of bps) {
      seed[bp] = (ResponsiveLayouts[bp] || []).map(pickRect);
    }
    lastLayoutsRef.current = seed;
  }, [isEditingUi, ResponsiveLayouts]);

  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    if (allLayouts && typeof allLayouts === 'object') {
      for (const bp of bps) {
        const arr = allLayouts[bp];
        if (Array.isArray(arr)) {
          lastLayoutsRef.current[bp] = arr.map(pickRect);
        }
      }
    } else if (Array.isArray(currentLayout)) {
      lastLayoutsRef.current[currentBp] = currentLayout.map(pickRect);
    }
  }, [currentBp]);

  const diffAndPersistAll = useCallback((layoutArr, bp) => {
    const prevById = Object.fromEntries(
      (layout?.gridLayout || []).map(e => [e.messageId, e[bp]])
    );
    const changedIds = [];
    for (const li of layoutArr || []) {
      const prev = prevById[li.i] || {};
      if (prev.x !== li.x || prev.y !== li.y || prev.w !== li.w || prev.h !== li.h) {
        dispatch(updateGridItemRect({
          messageId: li.i,
          breakpoint: bp,
          rect: { x: li.x, y: li.y, w: li.w, h: li.h }
        }));
        changedIds.push(li.i);
      }
    }
    changedIds.forEach(id => touchedMapRef.current.set(id, bp));
  }, [dispatch, layout]);

  const handleDragStop = useCallback(
    (layoutArr) => {
      if (!Array.isArray(layoutArr)) return;
      diffAndPersistAll(layoutArr, currentBp);
    },
    [diffAndPersistAll, currentBp]
  );

  const handleResizeStop = useCallback(
    (layoutArr) => {
      if (!Array.isArray(layoutArr)) return;
      diffAndPersistAll(layoutArr, currentBp);
    },
    [diffAndPersistAll, currentBp]
  );

  const LayoutAgentCard = () => (
    <div className="absolute top-4 right-4 z-[10002] flex items-center bg-white border border-gray-300 rounded-lg shadow px-4 py-2 space-x-2 animate-pulse opacity-95">
      <Squares2X2Solid className="h-5 w-5 text-indigo-600 flex-none" />
      <div className="flex flex-col leading-tight">
        <div className="flex items-baseline space-x-1 text-sm font-semibold">
          <span className="text-transparent bg-clip-text bg-blue-gradient-dark">Layout</span>
          <span className="bg-gradient-to-r from-pink-500/70 to-pink-600/90 bg-clip-text text-transparent font-bold">Agent</span>
        </div>
        <span className="text-xs text-gray-500">Updating&nbsp;layout&hellip;</span>
      </div>
    </div>
  );

  const buildFullGridFromSnapshots = useCallback(() => {
    const snaps = lastLayoutsRef.current;
    const snapBy = Object.fromEntries(bps.map(bp => [bp, rectsById(snaps[bp])]));

    const ids = new Set();
    bps.forEach(bp => (snaps[bp] || []).forEach(li => ids.add(li.i)));
    (layout?.gridLayout || []).forEach(e => ids.add(e.messageId));

    const result = [];
    for (const id of ids) {
      const existing = (layout?.gridLayout || []).find(e => e.messageId === id);
      const baseLg = snapBy.lg[id] || existing?.lg || { x: 0, y: 0, w: 12, h: 6 };

      const entry = {
        messageId: id,
        lg: baseLg,
        pinned: existing?.pinned === true,
        hidden: existing?.hidden === true,
      };
      for (const bp of ['md', 'sm', 'xs', 'xxs']) {
        const rect = snapBy[bp][id] || existing?.[bp];
        if (rect) {
          entry[bp] = rect;
        } else {
          const cols = COLS[bp];
          const w = Math.min(baseLg.w, cols);
          const x = Math.min(Math.max(0, baseLg.x), Math.max(0, cols - w));
          entry[bp] = { ...baseLg, w, x };
        }
      }
      result.push(entry);
    }
    return result;
  }, [layout]);

  const onDoneEditing = () => {
    const newGridLayout = buildFullGridFromSnapshots();
    dispatch(updateUiLayout({ gridLayout: newGridLayout }));
    if (socket?.emit) {
      socket.emit('manualUiChange', { layout: { gridLayout: newGridLayout } });
    }
    touchedMapRef.current.clear();
    dispatch(setIsEditingUi(false));
  };

  const EditToggle = () => (
    <button
      onClick={isEditingUi ? onDoneEditing : () => dispatch(setIsEditingUi(true))}
      className={`absolute top-3 right-4 inline-flex items-center gap-2 rounded-lg border px-3 py-2 shadow bg-white ${isEditingUi ? 'border-indigo-500' : 'border-gray-300'} hover:shadow-md active:scale-95 transition`}
      title={isEditingUi ? 'Finish editing' : 'Edit layout'}
    >
      {isEditingUi ? (
        <>
          <CheckIcon className="h-5 w-5 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-600">Done</span>
        </>
      ) : (
        <>
          <SquaresPlusIcon className="h-5 w-5 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-600">Edit</span>
        </>
      )}
    </button>
  );

  const pxHeightFor = useCallback((it) => {
    const r = it?.[currentBp] || it?.lg || { h: 6 };
    const h = Math.max(1, Number(r?.h) || 6);
    return h * ROW_HEIGHT + Math.max(0, h - 1) * MARGIN[1];
  }, [currentBp]);

  const renderCard = useCallback((item, extraClasses = '', lockCursor = false) => {
    const msg = msgLookup[item.messageId];
    if (!msg) return null;

    const Comp = {
      report: ReportCard,
      graph: GraphCard,
      grid: GridCard,
      record: RecordCard,
      number: NumberCard,
      spreadsheet: SpreadsheetCard,
      batchCreate: BatchCreateCard,
    }[msg.type] || (() => null);

    const isLinkedHighlight =
      hoverLink?.targetMessageId &&
      hoverLink.targetMessageId === item.messageId;

    const isPinned = !!item.pinned;

    return (
      <div
        key={item.messageId}
        className={[
          'h-full min-h-0 rounded-xl overflow-hidden transition-shadow',
          isLinkedHighlight
            ? 'ring-4 ring-indigo-300 ring-offset-2 ring-offset-white shadow-lg'
            : 'shadow-none',
          isPinned && lockCursor ? 'cursor-default' : '',
          extraClasses,
        ].join(' ')}
      >
        <Comp {...msg} />
      </div>
    );
  }, [hoverLink, msgLookup]);

  const showBottomSpacer = !showReasoning && !(isEditingUi && hiddenItems.length);

  return (
    <div className="relative flex flex-col h-full px-2 pt-4">
      <div ref={scrollRef} className={`p-0 h-full overflow-y-auto ${gridPaddingClass}`}>
        {isEmpty ? (
          <div className="w-full h-full flex flex-col items-center justify-center min-h-[70vh] space-y-6 pb-20">
            <h2 className="text-3xl italic font-serif text-gray-800 opacity-20 pl-1">
              let's get to work.
            </h2>
            <l-mirage class="tarn-glass" size="250" speed="5" color="#4F46E5"></l-mirage>
          </div>
        ) : (
          <div
            ref={frameRef}
            style={
              canEditNow
                ? { padding: `${CONTAINER_PAD[1]}px ${CONTAINER_PAD[0]}px`, ...editBgStyle }
                : { padding: `${CONTAINER_PAD[1]}px ${CONTAINER_PAD[0]}px` }
            }
          >
            <ResponsiveGridLayout
              breakpoints={BREAKPOINTS_PX}
              cols={COLS}
              layouts={ResponsiveLayouts}
              rowHeight={ROW_HEIGHT}
              margin={MARGIN}
              containerPadding={[0, 0]}
              compactType="vertical"
              isBounded={true}
              allowOverlap={false}
              useCSSTransforms={true}
              draggableCancel={'input,textarea,button,select,[data-no-drag="true"],.no-drag'}
              onBreakpointChange={bp => setCurrentBp(bp)}
              onLayoutChange={handleLayoutChange}
              onDragStop={handleDragStop}
              onResizeStop={handleResizeStop}
              isDraggable={canEditNow}
              isResizable={canEditNow}
              className="px-0"
            >
              {visibleItems.map(item => renderCard(item))}
            </ResponsiveGridLayout>

            {showBottomSpacer && <div aria-hidden style={{ height: BOTTOM_SPACER_PX }} />}
          </div>
        )}

        {isEditingUi && hiddenItems.length > 0 && (
          <HiddenCardTray
            hiddenItems={hiddenItems}
            pxHeightFor={pxHeightFor}
            renderCard={renderCard}
            scrollContainerRef={scrollRef}
          />
        )}
      </div>

      {showReasoning && (
        <div className="sticky bottom-4 left-0 w-full h-[45vh] flex justify-center z-[10001] pointer-events-auto">
          <ReasoningCard />
        </div>
      )}

      {isDashboard && !isLayoutGenerating && !isEmpty && <EditToggle />}

      {isLayoutGenerating && <LayoutAgentCard />}

      {isDashboard && !isGenerating && !isLayoutGenerating && !isEditingUi && (
        <button
          onClick={() => dispatch(setTrailOpen(true))}
          className="fixed bottom-6 left-6 z-[10003] h-14 w-14 rounded-full bg-white border border-gray-300 shadow-xl flex items-center justify-center hover:shadow-2xl active:scale-95 transition"
        >
          <QueueListSolid className="h-8 w-8 text-indigo-500" />
        </button>
      )}

      <Trail />
    </div>
  );
}

export default DynamicUI;